import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { formsAPI, patientsAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { ArrowLeft, Save, Loader2, Search, User } from 'lucide-react';
import { toast } from 'sonner';

export default function FormSubmitPage() {
  const navigate = useNavigate();
  const { templateId } = useParams();
  const [searchParams] = useSearchParams();
  const preSelectedPatientId = searchParams.get('patient');

  const [template, setTemplate] = useState(null);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(preSelectedPatientId || '');
  const [patientSearch, setPatientSearch] = useState('');
  const [formValues, setFormValues] = useState({});

  useEffect(() => {
    fetchData();
  }, [templateId]);

  const fetchData = async () => {
    try {
      const [templateRes, patientsRes] = await Promise.all([
        formsAPI.getTemplateById(templateId),
        patientsAPI.getAll({ limit: 100 }),
      ]);
      setTemplate(templateRes.data);
      setPatients(patientsRes.data);

      // Initialize form values with defaults
      const initialValues = {};
      templateRes.data.fields.forEach(field => {
        if (field.type === 'checkbox') {
          initialValues[field.name] = false;
        } else {
          initialValues[field.name] = field.default_value || '';
        }
      });
      setFormValues(initialValues);
    } catch (err) {
      toast.error('Failed to load form');
      navigate('/forms');
    }
    setLoading(false);
  };

  const handleValueChange = (fieldName, value) => {
    setFormValues(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedPatientId) {
      toast.error('Please select a patient');
      return;
    }

    // Validate required fields
    const missingRequired = template.fields
      .filter(f => f.required && !formValues[f.name])
      .map(f => f.label);

    if (missingRequired.length > 0) {
      toast.error(`Please fill in: ${missingRequired.join(', ')}`);
      return;
    }

    setSubmitting(true);
    try {
      await formsAPI.createSubmission({
        form_template_id: templateId,
        patient_id: selectedPatientId,
        data: formValues,
      });
      toast.success('Form submitted successfully');
      navigate(`/patients/${selectedPatientId}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit form');
    }
    setSubmitting(false);
  };

  const filteredPatients = patients.filter(p =>
    `${p.first_name} ${p.last_name}`.toLowerCase().includes(patientSearch.toLowerCase()) ||
    p.patient_id.toLowerCase().includes(patientSearch.toLowerCase())
  );

  const selectedPatient = patients.find(p => p.id === selectedPatientId);

  const renderField = (field) => {
    switch (field.type) {
      case 'text':
        return (
          <Input
            value={formValues[field.name] || ''}
            onChange={(e) => handleValueChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            data-testid={`field-${field.name}`}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={formValues[field.name] || ''}
            onChange={(e) => handleValueChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            rows={4}
            data-testid={`field-${field.name}`}
          />
        );

      case 'number':
        return (
          <Input
            type="number"
            value={formValues[field.name] || ''}
            onChange={(e) => handleValueChange(field.name, e.target.value)}
            placeholder={field.placeholder}
            data-testid={`field-${field.name}`}
          />
        );

      case 'date':
        return (
          <Input
            type="date"
            value={formValues[field.name] || ''}
            onChange={(e) => handleValueChange(field.name, e.target.value)}
            data-testid={`field-${field.name}`}
          />
        );

      case 'select':
        return (
          <Select
            value={formValues[field.name] || ''}
            onValueChange={(v) => handleValueChange(field.name, v)}
          >
            <SelectTrigger data-testid={`field-${field.name}`}>
              <SelectValue placeholder={field.placeholder || 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );

      case 'checkbox':
        return (
          <div className="flex items-center space-x-2">
            <Checkbox
              id={field.name}
              checked={formValues[field.name] || false}
              onCheckedChange={(checked) => handleValueChange(field.name, checked)}
              data-testid={`field-${field.name}`}
            />
            <label htmlFor={field.name} className="text-sm cursor-pointer">
              {field.label}
            </label>
          </div>
        );

      case 'radio':
        return (
          <RadioGroup
            value={formValues[field.name] || ''}
            onValueChange={(v) => handleValueChange(field.name, v)}
            data-testid={`field-${field.name}`}
          >
            {field.options?.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`${field.name}-${option}`} />
                <Label htmlFor={`${field.name}-${option}`}>{option}</Label>
              </div>
            ))}
          </RadioGroup>
        );

      default:
        return (
          <Input
            value={formValues[field.name] || ''}
            onChange={(e) => handleValueChange(field.name, e.target.value)}
            data-testid={`field-${field.name}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Form template not found</p>
        <Button onClick={() => navigate('/forms')} className="mt-4">
          Back to Forms
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6 animate-fadeIn" data-testid="form-submit">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{template.name}</h1>
          {template.description && (
            <p className="text-muted-foreground">{template.description}</p>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Patient Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Select Patient</CardTitle>
            <CardDescription>Choose the patient for this record</CardDescription>
          </CardHeader>
          <CardContent>
            {selectedPatient ? (
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="font-semibold text-sm text-primary">
                      {selectedPatient.first_name[0]}{selectedPatient.last_name[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium">
                      {selectedPatient.first_name} {selectedPatient.last_name}
                    </p>
                    <p className="text-sm text-muted-foreground">{selectedPatient.patient_id}</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedPatientId('')}
                >
                  Change
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search patients..."
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    className="pl-9"
                    data-testid="patient-search"
                  />
                </div>
                <div className="max-h-[200px] overflow-y-auto space-y-1">
                  {filteredPatients.slice(0, 10).map((patient) => (
                    <button
                      type="button"
                      key={patient.id}
                      className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-muted transition-colors text-left"
                      onClick={() => setSelectedPatientId(patient.id)}
                      data-testid={`select-patient-${patient.id}`}
                    >
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {patient.first_name} {patient.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground">{patient.patient_id}</p>
                      </div>
                    </button>
                  ))}
                  {filteredPatients.length === 0 && (
                    <p className="text-center py-4 text-muted-foreground">No patients found</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Form Fields */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Form Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {template.fields.map((field) => (
              <div key={field.name} className="space-y-2">
                {field.type !== 'checkbox' && (
                  <Label htmlFor={field.name}>
                    {field.label}
                    {field.required && <span className="text-destructive ml-1">*</span>}
                  </Label>
                )}
                {renderField(field)}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-6">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting} className="gap-2" data-testid="submit-form-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Submit Form
          </Button>
        </div>
      </form>
    </div>
  );
}
